import React, { useState } from 'react';
import { FileText, Upload, Download, Search, Folder, File, MoreVertical, Trash2, Eye, Edit, X, Share2, CheckCircle2, AlertCircle, Plus, Image as ImageIcon } from 'lucide-react';
import './DocumentsCard.css';

const DocumentsCard = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [uploadData, setUploadData] = useState({
    category: 'medical',
    description: '',
    tags: ''
  });
  const [shareData, setShareData] = useState({
    email: '',
    message: '',
    expiryDays: '7'
  });

  const categories = [
    { id: 'all', name: 'All Documents', count: 24 },
    { id: 'medical', name: 'Medical Records', count: 8 },
    { id: 'prescriptions', name: 'Prescriptions', count: 6 },
    { id: 'lab-results', name: 'Lab Results', count: 5 },
    { id: 'forms', name: 'Forms', count: 3 },
    { id: 'reports', name: 'Reports', count: 2 }
  ];

  const documents = [
    {
      id: 1,
      name: 'Blood Test Results - Dec 2024',
      type: 'lab-results',
      size: '2.4 MB',
      date: 'Dec 15, 2024',
      icon: 'pdf',
      category: 'Lab Results'
    },
    {
      id: 2,
      name: 'Prescription - Amoxicillin',
      type: 'prescriptions',
      size: '1.2 MB',
      date: 'Dec 14, 2024',
      icon: 'pdf',
      category: 'Prescriptions'
    },
    {
      id: 3,
      name: 'Medical History Form',
      type: 'forms',
      size: '856 KB',
      date: 'Dec 12, 2024',
      icon: 'doc',
      category: 'Forms'
    },
    {
      id: 4,
      name: 'X-Ray Report - Chest',
      type: 'reports',
      size: '5.7 MB',
      date: 'Dec 10, 2024',
      icon: 'image',
      category: 'Reports'
    },
    {
      id: 5,
      name: 'Insurance Card',
      type: 'medical',
      size: '1.8 MB',
      date: 'Dec 8, 2024',
      icon: 'image',
      category: 'Medical Records'
    },
    {
      id: 6,
      name: 'Vaccination Record',
      type: 'medical',
      size: '3.1 MB',
      date: 'Dec 5, 2024',
      icon: 'pdf',
      category: 'Medical Records'
    }
  ];

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || doc.type === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getFileIcon = (iconType) => {
    switch (iconType) {
      case 'pdf':
        return <FileText size={20} />;
      case 'doc':
        return <FileText size={20} />;
      case 'image':
        return <File size={20} />;
      default:
        return <File size={20} />;
    }
  };

  const handleFileUpload = (event) => {
    const files = event.target.files;
    if (files.length > 0) {
      setUploading(true);
      // Simulate upload process
      setTimeout(() => {
        setUploading(false);
        alert('File uploaded successfully!');
      }, 2000);
    }
  };

  const handleUploadClick = () => {
    setShowUploadModal(true);
  };

  const handleConfirmUpload = () => {
    console.log('Uploading with data:', uploadData);
    alert('Files uploaded successfully!');
    setShowUploadModal(false);
    setUploadData({
      category: 'medical',
      description: '',
      tags: ''
    });
  };

  const handleDownload = (document) => {
    console.log('Downloading:', document.name);
    alert(`Downloading ${document.name}`);
  };

  const handlePreview = (document) => {
    setSelectedDocument(document);
    setShowPreviewModal(true);
  };

  const handleShare = (document) => {
    setSelectedDocument(document);
    setShowShareModal(true);
  };

  const handleConfirmShare = () => {
    console.log('Sharing document:', {
      document: selectedDocument,
      ...shareData
    });
    alert(`Document shared with ${shareData.email}`);
    setShowShareModal(false);
    setShareData({
      email: '',
      message: '',
      expiryDays: '7'
    });
  };

  const handleDeleteClick = (document) => {
    setSelectedDocument(document);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    console.log('Deleting:', selectedDocument.name);
    alert(`${selectedDocument.name} has been deleted`);
    setShowDeleteModal(false);
    setSelectedDocument(null);
  };

  return (
    <div className="documents-container">
      <div className="documents-header">
        <div className="header-content">
          <h2>Document Management</h2>
          <p>Upload, organize, and access your medical documents securely</p>
        </div>
        <div className="upload-section">
          <button className="btn btn-gold" onClick={handleUploadClick}>
            <Upload size={16} />
            Upload Files
          </button>
        </div>
      </div>

      <div className="documents-content">
        <div className="documents-sidebar">
          <div className="search-container">
            <Search size={16} />
            <input
              type="text"
              placeholder="Search documents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="categories-list">
            <h3>Categories</h3>
            {categories.map((category) => (
              <button
                key={category.id}
                className={`category-item ${selectedCategory === category.id ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category.id)}
              >
                <Folder size={16} />
                <span className="category-name">{category.name}</span>
                <span className="category-count">{category.count}</span>
              </button>
            ))}
          </div>

          <div className="storage-info">
            <h4>Storage</h4>
            <div className="storage-bar">
              <div className="storage-progress" style={{ width: '65%' }}></div>
            </div>
            <p>6.5 GB of 10 GB used</p>
          </div>
        </div>

        <div className="documents-main">
          <div className="documents-toolbar">
            <div className="toolbar-info">
              <h3>
                {selectedCategory === 'all' ? 'All Documents' : 
                 categories.find(c => c.id === selectedCategory)?.name}
              </h3>
              <span className="document-count">{filteredDocuments.length} documents</span>
            </div>
            <div className="toolbar-actions">
              <button className="btn btn-outline">
                <Download size={16} /> Export All
              </button>
            </div>
          </div>

          <div className="documents-grid">
            {filteredDocuments.length > 0 ? (
              filteredDocuments.map((document) => (
                <div key={document.id} className="document-card">
                  <div className="document-icon">
                    {getFileIcon(document.icon)}
                  </div>
                  <div className="document-info">
                    <h4>{document.name}</h4>
                    <p className="document-category">{document.category}</p>
                    <div className="document-meta">
                      <span className="document-size">{document.size}</span>
                      <span className="document-date">{document.date}</span>
                    </div>
                  </div>
                  <div className="document-actions">
                    <button 
                      className="action-btn"
                      onClick={() => handlePreview(document)}
                      title="Preview"
                    >
                      <Eye size={16} />
                    </button>
                    <button 
                      className="action-btn"
                      onClick={() => handleDownload(document)}
                      title="Download"
                    >
                      <Download size={16} />
                    </button>
                    <button 
                      className="action-btn"
                      onClick={() => handleShare(document)}
                      title="Share"
                    >
                      <Share2 size={16} />
                    </button>
                    <button 
                      className="action-btn delete"
                      onClick={() => handleDeleteClick(document)}
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-documents">
                <FileText size={48} />
                <h3>No documents found</h3>
                <p>Try adjusting your search or upload new files</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="doc-modal-overlay" onClick={() => setShowUploadModal(false)}>
          <div className="doc-modal doc-modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="doc-modal-header">
              <h2>Upload Documents</h2>
              <button className="doc-modal-close" onClick={() => setShowUploadModal(false)}>
                <X size={24} />
              </button>
            </div>
            <div className="doc-modal-body">
              <div className="upload-zone">
                <input 
                  type="file" 
                  id="doc-file-upload" 
                  multiple 
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                />
                <label htmlFor="doc-file-upload" className="upload-zone-label">
                  <Upload size={40} />
                  <p><strong>Click to upload</strong> or drag and drop</p>
                  <span>PDF, DOC, DOCX, JPG, PNG (MAX. 10MB)</span>
                </label>
              </div>

              <div className="doc-form-group">
                <label>Category</label>
                <select 
                  value={uploadData.category}
                  onChange={(e) => setUploadData(prev => ({ ...prev, category: e.target.value }))}
                >
                  <option value="medical">Medical Records</option>
                  <option value="prescriptions">Prescriptions</option>
                  <option value="lab-results">Lab Results</option>
                  <option value="forms">Forms</option>
                  <option value="reports">Reports</option>
                </select>
              </div>

              <div className="doc-form-group">
                <label>Description (Optional)</label>
                <textarea 
                  rows="3"
                  placeholder="Add a description for these documents..."
                  value={uploadData.description}
                  onChange={(e) => setUploadData(prev => ({ ...prev, description: e.target.value }))}
                />
              </div>

              <div className="doc-form-group">
                <label>Tags (Optional)</label>
                <input 
                  type="text"
                  placeholder="e.g., urgent, follow-up, insurance (comma-separated)"
                  value={uploadData.tags}
                  onChange={(e) => setUploadData(prev => ({ ...prev, tags: e.target.value }))}
                />
              </div>
            </div>
            <div className="doc-modal-footer">
              <button className="doc-btn-outline" onClick={() => setShowUploadModal(false)}>Cancel</button>
              <button className="doc-btn-primary" onClick={handleConfirmUpload}>
                <Upload size={16} /> Upload Files
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && selectedDocument && (
        <div className="doc-modal-overlay" onClick={() => setShowPreviewModal(false)}>
          <div className="doc-modal doc-modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="doc-modal-header">
              <h2>Document Preview</h2>
              <button className="doc-modal-close" onClick={() => setShowPreviewModal(false)}>
                <X size={24} />
              </button>
            </div>
            <div className="doc-modal-body">
              <div className="doc-preview-info">
                <div className="preview-icon">
                  {getFileIcon(selectedDocument.icon)}
                </div>
                <div>
                  <h3>{selectedDocument.name}</h3>
                  <p>{selectedDocument.category}</p>
                  <div className="preview-meta">
                    <span>{selectedDocument.size}</span>
                    <span>•</span>
                    <span>{selectedDocument.date}</span>
                  </div>
                </div>
              </div>

              <div className="doc-preview-area">
                {selectedDocument.icon === 'image' ? (
                  <div className="preview-placeholder">
                    <ImageIcon size={64} />
                    <p>Image preview would appear here</p>
                  </div>
                ) : (
                  <div className="preview-placeholder">
                    <FileText size={64} />
                    <p>Document preview would appear here</p>
                    <span>Click download to view the full document</span>
                  </div>
                )}
              </div>
            </div>
            <div className="doc-modal-footer">
              <button className="doc-btn-outline" onClick={() => setShowPreviewModal(false)}>Close</button>
              <button className="doc-btn-outline" onClick={() => handleShare(selectedDocument)}>
                <Share2 size={16} /> Share
              </button>
              <button className="doc-btn-primary" onClick={() => handleDownload(selectedDocument)}>
                <Download size={16} /> Download
              </button>
            </div>
          </div>
        </div>
      )}

      {/*Share Modal */}
      {showShareModal && selectedDocument && (
        <div className="doc-modal-overlay" onClick={() => setShowShareModal(false)}>
          <div className="doc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="doc-modal-header">
              <h2>Share Document</h2>
              <button className="doc-modal-close" onClick={() => setShowShareModal(false)}>
                <X size={24} />
              </button>
            </div>
            <div className="doc-modal-body">
              <div className="share-doc-info">
                <FileText size={20} />
                <div>
                  <p><strong>{selectedDocument.name}</strong></p>
                  <span>{selectedDocument.category}</span>
                </div>
              </div>

              <div className="doc-form-group">
                <label>Recipient Email</label>
                <input 
                  type="email"
                  placeholder="Enter email address"
                  value={shareData.email}
                  onChange={(e) => setShareData(prev => ({ ...prev, email: e.target.value }))}
                />
              </div>

              <div className="doc-form-group">
                <label>Access Expiry</label>
                <select 
                  value={shareData.expiryDays}
                  onChange={(e) => setShareData(prev => ({ ...prev, expiryDays: e.target.value }))}
                >
                  <option value="1">1 day</option>
                  <option value="3">3 days</option>
                  <option value="7">7 days</option>
                  <option value="30">30 days</option>
                  <option value="never">Never</option>
                </select>
              </div>

              <div className="doc-form-group">
                <label>Message (Optional)</label>
                <textarea 
                  rows="4"
                  placeholder="Add a message to include with this document..."
                  value={shareData.message}
                  onChange={(e) => setShareData(prev => ({ ...prev, message: e.target.value }))}
                />
              </div>

              <div className="share-security-note">
                <AlertCircle size={18} color="#1f73e6" />
                <p>This document will be shared securely with encrypted access</p>
              </div>
            </div>
            <div className="doc-modal-footer">
              <button className="doc-btn-outline" onClick={() => setShowShareModal(false)}>Cancel</button>
              <button className="doc-btn-primary" onClick={handleConfirmShare} disabled={!shareData.email}>
                <Share2 size={16} /> Share Document
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedDocument && (
        <div className="doc-modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="doc-modal doc-modal-warning" onClick={(e) => e.stopPropagation()}>
            <div className="doc-modal-header">
              <h2>Delete Document?</h2>
              <button className="doc-modal-close" onClick={() => setShowDeleteModal(false)}>
                <X size={24} />
              </button>
            </div>
            <div className="doc-modal-body">
              <div className="delete-warning">
                <AlertCircle size={32} color="#ef4444" />
                <p><strong>Are you sure you want to delete this document?</strong></p>
                <p className="warning-text">This action cannot be undone.</p>
              </div>

              <div className="delete-doc-info">
                <FileText size={20} />
                <div>
                  <p><strong>{selectedDocument.name}</strong></p>
                  <span>{selectedDocument.category} • {selectedDocument.size}</span>
                </div>
              </div>
            </div>
            <div className="doc-modal-footer">
              <button className="doc-btn-outline" onClick={() => setShowDeleteModal(false)}>Keep Document</button>
              <button className="doc-btn-danger" onClick={handleConfirmDelete}>
                <Trash2 size={16} /> Delete Permanently
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentsCard;
